import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Subject,
  concat,
  concatMap,
  defer,
  forkJoin,
  from,
  of,
  takeUntil
} from 'rxjs';
import { BusinessComplianceSettingForm } from 'src/app/classes/form-groups/business-compliance-setting-form';
import { ComplianceSettingForm } from 'src/app/classes/form-groups/compliance-setting-form';
import { LoadingMask } from 'src/app/classes/loading-mask';
import { Company, User } from 'src/app/classes/models';
import { BusinessComplianceSetting } from 'src/app/classes/models/business-compliance-setting';
import { CompanyComplianceType } from 'src/app/classes/models/company-compliance-type';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { ComplianceSettingLiabilityType } from 'src/app/interfaces/compliance-setting-liability-types';
import { ConfigureComplianceTypesModalComponent } from 'src/app/modals/configure-compliance-types-modal/configure-compliance-types-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { GetUserInputModalComponent } from 'src/app/modals/get-user-input-modal/get-user-input-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';

@Component({
  selector: 'app-compliance-settings',
  templateUrl: './compliance-settings.component.html',
  styleUrls: ['./compliance-settings.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceSettingsComponent implements OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private CompanyService: CompanyService,
    private ComplianceService: ComplianceService,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private SharedDataService: SharedDataService,
    private HostElement: ElementRef,
    private AuthService: AuthService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {
    const observableArr = [];
    const paramMap = this.route.snapshot.paramMap;
    const CompanyId = Number(paramMap.get('CompanyId'));
    this.CompanyId = CompanyId;

    this.LoggedInUser = this.AuthService.getUser();

    from(
      companiesService.findOneById({
        pathParams: { companyId: this.CompanyId }
      })
    ).subscribe({
      next: (Company) => {
        const { legalName } = Company;
        this.SharedDataService.sendEvent(
          'SetTitle',
          `Compliance - ${legalName}`
        );
        this.ChangeDetectorRef.markForCheck();
      }
    });

    observableArr.push(
      defer(() => {
        return this.loadComplianceTypes();
      })
    );

    observableArr.push(
      defer(() => {
        return this.loadComplianceSettings().pipe(
          concatMap((company: Company) => {
            this.company = company;

            const {
              ComplianceSettings,
              hasGlobalCompliance,
              BusinessComplianceSettings,
              usesNewBusinessVerification,
              allowAutomatedBusinessVerificationClientRegisteredVendors
            } = this.company;

            if (hasGlobalCompliance) {
              this.hasGlobalCompliance = hasGlobalCompliance;
            }

            if (BusinessComplianceSettings) {
              this.businessComplianceSettings = BusinessComplianceSettings;

              this.businessComplianceSettings.forEach((setting) => {
                const form = new BusinessComplianceSettingForm();
                form.patchValue(setting);

                if (!this.isAllowedToEditSettings()) {
                  form.disable();
                }

                this.businessComplianceForms.push(form);
              });

              this.businessComplianceForms.forEach((form, index) => {
                form.valueChanges
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((change) => {
                    if (this.businessComplianceSettings) {
                      Object.assign(
                        this.businessComplianceSettings[index],
                        change
                      );
                    }
                  });
              });
            }

            if (
              (!BusinessComplianceSettings ||
                BusinessComplianceSettings.length === 0) &&
              (usesNewBusinessVerification ||
                allowAutomatedBusinessVerificationClientRegisteredVendors)
            ) {
              this.generateMissingBusinessComplianceSettingForm();
            }

            if (ComplianceSettings) {
              this.complianceSettings = ComplianceSettings;

              this.formatComplianceSettings();

              this.complianceSettings.forEach((cs) => {
                const csForm = new ComplianceSettingForm();
                csForm.patchValue(cs);

                if (!this.isAllowedToEditSettings()) {
                  csForm.disable();
                }

                this.complianceSettingForms.push(csForm);
              });

              this.complianceSettingForms.forEach((form, index) => {
                form.valueChanges
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((change) => {
                    if (this.complianceSettings) {
                      Object.assign(this.complianceSettings[index], change);
                    }
                  });
              });

              this.setRiskLevels();
            }

            if (!Array.isArray(this.complianceSettings)) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Compliance Settings is not an array.',
                { returnError: true }
              );
            }

            if (!this.selectedComplianceType) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing selected compliance type.',
                { returnError: true }
              );
            }

            this.generateMissingComplianceSettingForms();

            this.ChangeDetectorRef.markForCheck();

            return of(company);
          })
        );
      })
    );

    concat(...observableArr).subscribe();
  }

  // For handling component destroy
  destroy$ = new Subject<void>();

  CompanyId?: number;
  company?: Company;
  complianceSettings?: ComplianceSetting[];
  complianceSetting?: ComplianceSetting;
  businessComplianceSettings?: BusinessComplianceSetting[];
  companyComplianceTypes?: Company['CompanyComplianceTypes'];
  defaultCompanyComplianceType?: CompanyComplianceType;
  selectedComplianceType?: CompanyComplianceType;
  complianceSettingForms: ComplianceSettingForm[] = [];
  businessComplianceForms: BusinessComplianceSettingForm[] = [];
  hasGlobalCompliance: boolean = false;

  // Used to prevent double clicking.
  enableRiskLevel2: boolean = false;
  enableRiskLevel3: boolean = false;

  // Shared value maps amongst liability sections
  amBestValueMap: Map<string, string | null> = new Map([
    ['A++', 'A++'],
    ['A+', 'A+'],
    ['A', 'A'],
    ['A-', 'A-'],
    ['B++', 'B++'],
    ['B+', 'B+'],
    ['B', 'B'],
    ['C++', 'C++'],
    ['C+', 'C+'],
    ['C', 'C'],
    ['C-', 'C-'],
    ['D', 'D'],
    ['E', 'E'],
    ['F', 'F'],
    ['S', 'S'],
    ['Not Rated', 'Not Rated'],
    ['Not Required', null]
  ]);

  amBestFinancialSize: Map<string, { value: string | null; hint: string }> =
    new Map([
      ['Not Required', { value: null, hint: 'No PHS Requirement' }],
      [
        'I',
        { value: 'I', hint: 'Policy Holder Surplus ($millions) [less than 1]' }
      ],
      [
        'II',
        { value: 'II', hint: 'Policy Holder Surplus ($millions) [1 < PHS < 2]' }
      ],
      [
        'III',
        {
          value: 'III',
          hint: 'Policy Holder Surplus ($millions) [2 < PHS < 5]'
        }
      ],
      [
        'IV',
        {
          value: 'IV',
          hint: 'Policy Holder Surplus ($millions) [5 < PHS < 10]'
        }
      ],
      [
        'V',
        {
          value: 'V',
          hint: 'Policy Holder Surplus ($millions) [10 < PHS < 25]'
        }
      ],
      [
        'VI',
        {
          value: 'VI',
          hint: 'Policy Holder Surplus ($millions) [25 < PHS < 50]'
        }
      ],
      [
        'VII',
        {
          value: 'VII',
          hint: 'Policy Holder Surplus ($millions) [50 < PHS < 100]'
        }
      ],
      [
        'VIII',
        {
          value: 'VIII',
          hint: 'Policy Holder Surplus ($millions) [100 < PHS < 250]'
        }
      ],
      [
        'IX',
        {
          value: 'IX',
          hint: 'Policy Holder Surplus ($millions) [250 < PHS < 500]'
        }
      ],
      [
        'X',
        {
          value: 'X',
          hint: 'Policy Holder Surplus ($millions) [500 < PHS < 750]'
        }
      ],
      [
        'XI',
        {
          value: 'XI',
          hint: 'Policy Holder Surplus ($millions) [750 < PHS < 1B]'
        }
      ],
      [
        'XII',
        {
          value: 'XII',
          hint: 'Policy Holder Surplus ($billions) [1 < PHS < 1.25]'
        }
      ],
      [
        'XIII',
        {
          value: 'XIII',
          hint: 'Policy Holder Surplus ($millions) [1.25 < PHS < 1.5]'
        }
      ],
      [
        'XIV',
        {
          value: 'XIV',
          hint: 'Policy Holder Surplus ($millions) [1.5 < PHS < 2]'
        }
      ],
      [
        'XV',
        { value: 'XV', hint: 'Policy Holder Surplus ($millions) [PHS = 2+]' }
      ]
    ]);

  amountPerOccurrenceMap: Map<string, number | null> =
    this.UtilityService.setCoverageAmounts(
      100000,
      50000,
      20000000,
      new Map([['Not Required', null]]),
      new Map([['$20,000,000+', 10000001]])
    );
  amountAggregateMap: Map<string, number | null> =
    this.UtilityService.setCoverageAmounts(
      100000,
      50000,
      20000000,
      new Map([['Not Required', null]]),
      new Map([['$20,000,000+', 10000001]])
    );
  LoggedInUser: User;

  generateComplianceSettingForms(
    riskLevelArray: number[],
    CompanyComplianceTypeId: number
  ) {
    riskLevelArray.forEach((riskLevel) => {
      const csForm = new ComplianceSettingForm();
      csForm.patchValue({
        CompanyId: this.CompanyId,
        CompanyComplianceTypeId,
        riskLevel: riskLevel
      });
      this.complianceSettingForms.push(csForm);

      const { value } = csForm;
      this.complianceSettings!.push(value);
    });
  }

  generateBusinessComplianceSettingForm(CompanyComplianceTypeId: number) {
    const form = new BusinessComplianceSettingForm();
    form.patchValue({
      CompanyId: this.CompanyId,
      CompanyComplianceTypeId
    });
    this.businessComplianceForms.push(form);
    const { value } = form;
    this.businessComplianceSettings!.push(value);
  }

  generateMissingComplianceSettingForms() {
    if (!Array.isArray(this.complianceSettings)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Compliance Settings is not an array.',
        { returnError: true }
      );
    }

    if (!this.selectedComplianceType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing selected compliance type.',
        { returnError: true }
      );
    }

    const { id: CompanyComplianceTypeId } = this.selectedComplianceType;

    if (!CompanyComplianceTypeId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Company Compliance Type Id.',
        { returnError: true }
      );
    }

    if (
      Array.isArray(this.complianceSettings) &&
      this.complianceSettings.length === 0
    ) {
      if (this.hasGlobalCompliance) {
        this.generateComplianceSettingForms([0], CompanyComplianceTypeId);
      } else {
        this.generateComplianceSettingForms([1, 2], CompanyComplianceTypeId);
      }
    }

    if (this.hasGlobalCompliance) {
      const GlobalComplianceSetting = this.complianceSettings.find(
        (cs) =>
          cs.riskLevel === 0 &&
          cs.CompanyComplianceTypeId === CompanyComplianceTypeId
      );

      if (!GlobalComplianceSetting) {
        this.generateComplianceSettingForms([0], CompanyComplianceTypeId);
      }
    } else {
      const ComplianceSettingRiskLevel1 = this.complianceSettings.find(
        (cs) =>
          cs.riskLevel === 1 &&
          cs.CompanyComplianceTypeId === CompanyComplianceTypeId
      );
      const ComplianceSettingRiskLevel2 = this.complianceSettings.find(
        (cs) =>
          cs.riskLevel === 2 &&
          cs.CompanyComplianceTypeId === CompanyComplianceTypeId
      );

      if (!ComplianceSettingRiskLevel1) {
        this.generateComplianceSettingForms([1], CompanyComplianceTypeId);
      }

      if (!ComplianceSettingRiskLevel2) {
        this.generateComplianceSettingForms([2], CompanyComplianceTypeId);
      }
    }
  }

  generateMissingBusinessComplianceSettingForm() {
    if (!Array.isArray(this.businessComplianceSettings)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Business Compliance Settings is not an array.',
        { returnError: true }
      );
    }

    if (!this.selectedComplianceType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing selected compliance type.',
        { returnError: true }
      );
    }

    const { id: CompanyComplianceTypeId } = this.selectedComplianceType;

    if (!CompanyComplianceTypeId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Company Compliance Type Id.',
        { returnError: true }
      );
    }

    this.generateBusinessComplianceSettingForm(CompanyComplianceTypeId);
  }

  isAllowedToEditSettings() {
    if (this.LoggedInUser) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      if (!role) {
        throw new Error('Missing user permission.');
      }

      return new Set([
        'ADMIN_ADMIN',
        'ADMIN_CUSTOMER_SERVICE_TEAM',
        'ADMIN_PROJECT_MANAGEMENT_TEAM'
      ]).has(role);
    }

    return false;
  }

  formatComplianceSettings() {
    if (!this.complianceSettings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot format compliance settings. No compliance settings found.',
        { returnError: true }
      );
    }

    if (this.hasGlobalCompliance) {
      this.complianceSettings = this.complianceSettings.filter(
        (setting) => setting.riskLevel === 0
      );
    } else {
      this.complianceSettings = this.complianceSettings.filter(
        (setting) => setting.riskLevel !== 0
      );
    }

    this.complianceSettings.sort((a, b) => a.riskLevel! - b.riskLevel!);
  }

  loadComplianceTypes() {
    return this.CompanyService.getCompanyComplianceTypes({
      CompanyId: this.CompanyId
    }).pipe(
      concatMap((companyComplianceTypes: CompanyComplianceType[]) => {
        this.companyComplianceTypes = companyComplianceTypes;
        this.defaultCompanyComplianceType = companyComplianceTypes.find(
          (type) => type.type === 'default'
        );
        this.selectedComplianceType = this.defaultCompanyComplianceType;

        return of(companyComplianceTypes);
      })
    );
  }

  loadComplianceSettings(complianceType?: CompanyComplianceType) {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'No Company ID.', {
        returnError: true
      });
    }

    const complianceSettingQuery = {
      TradeSpecificCompliance: {
        PrimaryTrade: {}
      },
      literals: {
        $and: ['globalComplianceRiskLevel']
      }
    };

    const businessComplianceSettingQuery = {};

    if (complianceType) {
      Object.assign(complianceSettingQuery, {
        CompanyComplianceTypeId: complianceType.id
      });

      Object.assign(businessComplianceSettingQuery, {
        CompanyComplianceTypeId: complianceType.id
      });
    } else {
      if (!this.defaultCompanyComplianceType) {
        throw this.ToastService.addErrorToast(
          'Error',
          'No default compliance type.',
          { returnError: true }
        );
      }

      Object.assign(complianceSettingQuery, {
        CompanyComplianceTypeId: this.defaultCompanyComplianceType.id
      });

      Object.assign(businessComplianceSettingQuery, {
        CompanyComplianceTypeId: this.defaultCompanyComplianceType.id
      });
    }

    return this.CompanyService.getCompany(
      this.CompanyId,
      this.UtilityService.stringifyObjectsInQueryObject({
        excludedAttributes: ['logo'],
        ComplianceSetting: complianceSettingQuery,
        BusinessComplianceSetting: businessComplianceSettingQuery,
        CompanyOperationState: {
          options: { separate: true },
          SubNationalDivision: { options: { required: true } }
        }
      })
    );
  }

  setRiskLevels() {
    if (!this.complianceSettings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No compliance settings.',
        { returnError: true }
      );
    }

    this.enableRiskLevel2 = this.complianceSettings.find(
      (setting) => setting.riskLevel === 2
    )
      ? true
      : false;
    this.enableRiskLevel3 = this.complianceSettings.find(
      (setting) => setting.riskLevel === 3
    )
      ? true
      : false;
  }

  onEnableRiskLevelChange(riskLevel: 2 | 3) {
    if (!this.complianceSettings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No compliance settings found.',
        { returnError: true }
      );
    }

    const enableRiskLevel =
      riskLevel === 2 ? this.enableRiskLevel2 : this.enableRiskLevel3;
    const complianceSettingIndex = this.complianceSettings.findIndex(
      (setting) => setting.riskLevel === riskLevel
    );
    const foundComplianceSetting = this.complianceSettings.find(
      (setting) => setting.riskLevel === riskLevel
    );

    if (!enableRiskLevel) {
      if (complianceSettingIndex !== -1) {
        const complianceSetting =
          this.complianceSettings[complianceSettingIndex];

        if (!complianceSetting || !complianceSetting.id) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Compliance Setting found.',
            { returnError: true }
          );
        }

        const { nativeElement } = this.HostElement;
        const mask = new LoadingMask(nativeElement, {
          maskText: 'Updating Compliance Settings...'
        });

        this.ComplianceService.deleteComplianceSetting(
          complianceSetting.id
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              `Disabled Risk Level ${riskLevel}.`
            );

            this.loadComplianceSettings(this.selectedComplianceType)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (company: Company) => {
                  this.company = company;

                  const { ComplianceSettings } = this.company;

                  if (ComplianceSettings) {
                    this.complianceSettings = ComplianceSettings;
                    this.formatComplianceSettings();
                    this.updateComplianceSettingsForm();
                    this.setRiskLevels();
                    this.checkIsRequired();
                  }

                  this.ChangeDetectorRef.markForCheck();
                  mask.destroy();
                },
                error: () => mask.destroy()
              });
          },
          error: () => mask.destroy()
        });
      }
    } else if (enableRiskLevel) {
      if (!this.selectedComplianceType) {
        throw this.ToastService.addErrorToast(
          'Error',
          'No selected compliance type found.'
        );
      }

      const { componentInstance, closed } = this.ModalService.open(
        ConfirmModalComponent,
        { backdrop: 'static', centered: true, size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText: `Are you sure you want to enable Risk Level ${riskLevel}? Doing so will reset trade tiers to default settings.`
      });

      closed.subscribe({
        next: (confirm) => {
          if (confirm) {
            if (riskLevel === 2) {
              this.enableRiskLevel2 = true;
            } else {
              this.enableRiskLevel3 = true;
            }

            const newComplianceSetting: ComplianceSetting = {
              CompanyId: this.CompanyId,
              riskLevel,
              CompanyComplianceTypeId: this.selectedComplianceType!.id
            };

            if (!foundComplianceSetting) {
              this.complianceSettings!.push(newComplianceSetting);
            }

            this.saveComplianceSettings();
          } else {
            if (riskLevel === 2) {
              this.enableRiskLevel2 = !this.enableRiskLevel2;
            } else {
              this.enableRiskLevel3 = !this.enableRiskLevel3;
            }
          }
        }
      });
    }
  }

  updateComplianceSettingsForm() {
    if (!this.complianceSettings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No compliance settings found.',
        { returnError: true }
      );
    }

    this.complianceSettingForms = [];
    this.businessComplianceForms = [];

    this.complianceSettings.forEach((cs) => {
      const newComplianceSettingForm = new ComplianceSettingForm();
      newComplianceSettingForm.patchValue(cs);

      if (!this.isAllowedToEditSettings()) {
        newComplianceSettingForm.disable();
      }

      this.complianceSettingForms.push(newComplianceSettingForm);
    });

    (this.businessComplianceSettings || []).forEach((bcs) => {
      const NewBusinessComplianceSettingForm =
        new BusinessComplianceSettingForm();
      NewBusinessComplianceSettingForm.patchValue(bcs);

      if (!this.isAllowedToEditSettings()) {
        NewBusinessComplianceSettingForm.disable();
      }

      this.businessComplianceForms.push(NewBusinessComplianceSettingForm);
    });

    this.complianceSettingForms.sort(
      (a, b) => a.controls.riskLevel.value! - b.controls.riskLevel.value!
    );

    this.complianceSettingForms.forEach((form, index) => {
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((change) => {
        if (this.complianceSettings) {
          Object.assign(this.complianceSettings[index], change);
        }
      });
    });

    this.businessComplianceForms.forEach((form, index) => {
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((change) => {
        if (this.businessComplianceSettings) {
          Object.assign(this.businessComplianceSettings[index], change);
        }
      });
    });
  }

  onComplianceTypeChange() {
    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Updating Compliance Settings...'
    });

    this.loadComplianceSettings(this.selectedComplianceType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (company: Company) => {
          this.company = company;

          const {
            usesNewBusinessVerification,
            allowAutomatedBusinessVerificationClientRegisteredVendors,
            ComplianceSettings,
            BusinessComplianceSettings
          } = this.company;

          this.complianceSettings = ComplianceSettings || [];
          this.businessComplianceSettings = BusinessComplianceSettings || [];

          if (this.complianceSettings.length === 0) {
            this.generateMissingComplianceSettingForms();
          }

          if (
            (usesNewBusinessVerification ||
              allowAutomatedBusinessVerificationClientRegisteredVendors) &&
            this.businessComplianceSettings.length === 0
          ) {
            this.generateMissingBusinessComplianceSettingForm();
          }

          this.formatComplianceSettings();
          this.updateComplianceSettingsForm();
          this.setRiskLevels();
          this.checkIsRequired();

          this.ChangeDetectorRef.markForCheck();
          mask.destroy();
        },
        error: () => mask.destroy()
      });
  }

  saveComplianceSettings() {
    const observableArr = [];

    if (!this.complianceSettings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save compliance settings.',
        { returnError: true }
      );
    }

    if (!this.complianceSettingForms) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No compliance setting forms',
        { returnError: true }
      );
    }

    // observableArr.push(
    //   defer(() => {
    //     return this.saveCompanyComplianceType();
    //   })
    // );

    observableArr.push(
      defer(() => {
        if (!this.complianceSettings) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No compliance settings found.',
            { returnError: true }
          );
        }

        return forkJoin(
          this.complianceSettings.map((compSetting) =>
            this.ComplianceService.saveComplianceSetting(compSetting)
          )
        ).pipe(
          concatMap((ComplianceSetting) => {
            if (ComplianceSetting) {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully saved compliance setting.'
              );

              const { nativeElement } = this.HostElement;
              const mask = new LoadingMask(nativeElement, {
                maskText: 'Updating Compliance Settings...'
              });

              this.loadComplianceSettings(this.selectedComplianceType)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (company: Company) => {
                    this.company = company;
                    mask.destroy();

                    const { ComplianceSettings, hasGlobalCompliance } =
                      this.company;

                    if (hasGlobalCompliance) {
                      this.hasGlobalCompliance = hasGlobalCompliance;
                    }

                    if (ComplianceSettings) {
                      this.complianceSettings = ComplianceSettings;
                      this.formatComplianceSettings();

                      this.complianceSettingForms = this.complianceSettings.map(
                        (setting) => {
                          const form = new ComplianceSettingForm();
                          form.patchValue(setting);

                          if (!this.isAllowedToEditSettings()) {
                            form.disable();
                          }

                          return form;
                        }
                      );

                      this.complianceSettingForms.forEach((form, index) => {
                        form.valueChanges
                          .pipe(takeUntil(this.destroy$))
                          .subscribe((change) => {
                            if (this.complianceSettings) {
                              Object.assign(
                                this.complianceSettings[index],
                                change
                              );
                            }
                          });
                      });

                      this.setRiskLevels();
                    }

                    this.ChangeDetectorRef.markForCheck();
                  },
                  error: () => mask.destroy()
                });
            }

            return of(this.complianceSetting);
          })
        );
      })
    );

    if (
      this.company &&
      (this.company.allowAutomatedBusinessVerificationClientRegisteredVendors ||
        this.company.usesNewBusinessVerification)
    ) {
      observableArr.push(
        defer(() => this.saveBusinessVerifiedComplianceSettings())
      );
    }

    concat(...observableArr).subscribe();
  }

  saveComplianceSetting(complianceSetting: ComplianceSetting) {
    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Saving Compliance Setting...'
    });
    return this.ComplianceService.saveComplianceSetting(complianceSetting)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ComplianceSetting: ComplianceSetting) => {
          mask.destroy();
        },
        error: () => mask.destroy()
      });
  }

  saveBusinessVerifiedComplianceSettings() {
    if (!this.company || !this.company.id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save Business Compliance Settings. No selected company found.',
        { returnError: true }
      );
    }

    if (this.businessComplianceForms.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save Business Compliance Settings. No selected Business Compliance Settings found.',
        { returnError: true }
      );
    }

    return forkJoin(
      this.businessComplianceForms.map(
        (setting: BusinessComplianceSettingForm) =>
          this.ComplianceService.saveBusinessComplianceSetting(
            this.company?.id!,
            setting.value
          )
      )
    );
  }

  // saveCompanyComplianceType() {
  //   if (!this.selectedComplianceType) {
  //     throw this.ToastService.addErrorToast('Error', 'Cannot save company compliance type. No selected company compliance type.');
  //   }

  //   return this.CompanyService.saveCompanyComplianceType(this.selectedComplianceType);
  // }

  conFigureComplianceType() {
    const modalRef = this.ModalService.open(
      ConfigureComplianceTypesModalComponent,
      { size: 'md' }
    );
    modalRef.componentInstance.CompanyId = this.CompanyId;
    modalRef.componentInstance.companyComplianceTypes =
      this.companyComplianceTypes;

    modalRef.closed.subscribe({
      next: (result) => {
        if (result) {
          this.loadComplianceTypes().subscribe();
        }
      }
    });
  }

  copyUrl(url: string | null) {
    this.UtilityService.copyUrl(url);
  }

  updateUrl(url: string | null, complianceSetting: ComplianceSetting) {
    if (!this.complianceSettings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No compliance settings found.'
      );
    }

    const modalRef = this.ModalService.open(GetUserInputModalComponent, {
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.title = 'Update URL';
    modalRef.componentInstance.message = 'Please update url.';
    modalRef.componentInstance.inputValue = url;

    modalRef.closed.pipe(takeUntil(this.destroy$)).subscribe({
      next: (updatedUrl: string) => {
        const selectedComplianceSetting = this.complianceSettings!.find(
          (setting) => setting.id === complianceSetting.id
        );

        if (selectedComplianceSetting) {
          Object.assign(selectedComplianceSetting, { sampleCert: updatedUrl });
          this.saveComplianceSetting(selectedComplianceSetting);
        }
      }
    });
  }

  checkIsRequired(liabilityType?: ComplianceSettingLiabilityType) {
    if (!this.complianceSettingForms) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No compliance setting forms found.',
        { returnError: true }
      );
    }

    const liabilityFormsMap = new Map<string, string[]>([
      [
        'generalLiability',
        [
          'generalLiability_isRequired',
          'generalLiability_amBestRating',
          'generalLiability_AllowNRRatedAMBestCarriers',
          'generalLiability_financialSizeCategory',
          'generalLiability_policyAmountPerOccurance',
          'generalLiability_policyAmountAggregate',
          'generalLiability_productOps',
          'generalLiability_medicalExpenses',
          'personalAdvertisementInjury',
          'generalLiability_damageToRentedPremises',
          'generalLiability_nonOwnedAutoAmount',
          'glIsNamed',
          'generalLiability_hasAiOngoingOps',
          'generalLiability_hasAiCompOps',
          'generalLiability_hasPnc',
          'glIsAgency',
          'glDescOps',
          'generalLiability_contractualLiability',
          'generalLiability_hasFullPolicyDoc',
          'generalLiability_hasRequiredForms',
          'generalLiability_isPerOccurrence',
          'generalLiability_perProjLocAgg',
          'glAdditionalInsuredEndorsements',
          'generalLiability_legalNameMismatch'
        ]
      ],
      [
        'autoLiability',
        [
          'autoLiability_isRequired',
          'autoLiability_amBestRating',
          'autoLiability_AllowNRRatedAMBestCarriers',
          'autoLiability_financialSizeCategory',
          'autoLiability_combinedSingleLimit',
          'autoLiability_propertyDamageLimit',
          'autoLiability_bodilyInjuryPerPerson',
          'autoLiability_bodilyInjuryPerAccident',
          'autoIsNamed',
          'autoIsAgency',
          'autoDescOps',
          'allowNonCommercialPolicy',
          'autoLiability_hasNonOwnedAuto',
          'autoLiability_hasFullPolicyDoc',
          'autoLiability_hasRequiredForms',
          'autoLiability_legalNameMismatch'
        ]
      ],
      [
        'umbrellaLiability',
        [
          'umbrellaLiability_isRequired',
          'umbrellaLiability_amBestRating',
          'umbrellaLiability_AllowNRRatedAMBestCarriers',
          'umbrellaLiability_financialSizeCategory',
          'umbrellaLiability_policyAmountPerOccurance',
          'umbrellaLiability_policyAmountAggregate',
          'umbIsNamed',
          'umbIsAgency',
          'umbDescOps',
          'umbrellaLiability_hasFullPolicyDoc',
          'umbrellaLiability_hasRequiredForms',
          'umbrellaLiability_primaryNonContributory',
          'umbrellaLiability_occurrenceBased',
          'umbrellaLiability_legalNameMismatch'
        ]
      ],
      [
        'workersCompensation',
        [
          'workersCompensation_isRequired',
          'workersCompensation_amBestRating',
          'workersCompensation_AllowNRRatedAMBestCarriers',
          'workersCompensation_financialSizeCategory',
          'workersCompensation_eachIncidentEmployersLimit',
          'workersCompensation_eachEmployeeMinimum',
          'workersCompensation_eachEmployerMinimum',
          'wkIsAgency',
          'wkDescOps',
          'workersCompensation_hasRequiredForms',
          'workersCompensation_officerExclusionNotAllowed',
          'disallowWCExemptions',
          'workersCompensation_legalNameMismatch'
        ]
      ],
      [
        'professionalLiability',
        [
          'errorsAndOmissions_isRequired',
          'errorsAndOmissions_amBestRating',
          'pl_AllowNRRatedAMBestCarriers',
          'errorsAndOmissions_financialSizeCategory',
          'errorsAndOmissions_policyAmount',
          'errIsNamed',
          'errIsAgency',
          'errDescOps',
          'professionalLiability_legalNameMismatch'
        ]
      ]
    ]);

    const updateComplianceSettingsForms = (
      isRequiredField: string | undefined,
      type: string
    ) => {
      if (!isRequiredField) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot check is required. No field to check.',
          { returnError: true }
        );
      }

      this.complianceSettingForms.forEach((form) => {
        const isRequiredControl =
          form.controls[
            isRequiredField as keyof ComplianceSettingForm['controls']
          ];

        if (isRequiredControl) {
          const isRequired = isRequiredControl.value;

          const controlNames = liabilityFormsMap.get(type) || [];

          controlNames.forEach((controlName) => {
            if (controlName !== isRequiredField) {
              const control =
                form.controls[
                  controlName as keyof ComplianceSettingForm['controls']
                ];
              if (control) {
                if (!isRequired) {
                  control.disable();
                } else {
                  control.enable();
                }
              }
            }
          });
        }
      });
    };

    if (liabilityType && liabilityFormsMap.has(liabilityType)) {
      const isRequiredField = liabilityFormsMap
        .get(liabilityType)
        ?.find((field) => field.includes('_isRequired'));

      updateComplianceSettingsForms(isRequiredField, liabilityType);
    } else {
      liabilityFormsMap.forEach((value, key) => {
        const isRequiredField = liabilityFormsMap
          .get(key)
          ?.find((field) => field.includes('_isRequired'));

        updateComplianceSettingsForms(isRequiredField, key);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
